"use client";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BiCloset, BiHomeAlt, BiUser } from "react-icons/bi";
import {
  BsClipboardData,
  BsBriefcase,
  BsChatSquare,
  BsBack,
} from "react-icons/bs";
import { HiChevronDoubleLeft, HiChevronDoubleRight } from "react-icons/hi";
import { Link } from "react-scroll";
import Lottie from "lottie-react";
import navToggleAnim from "../assets/lottie/AnimatedWhatesapp.json"; // adjust path
import { FaBackward, FaForward, FaRegClosedCaptioning } from "react-icons/fa";

const navItems = [
  { to: "home", icon: <BiHomeAlt />, offset: -200 },
  { to: "about", icon: <BiUser /> },
  { to: "services", icon: <BsClipboardData /> },
  { to: "work", icon: <BsBriefcase /> },
  { to: "contact", icon: <BsChatSquare /> },
];

const Nav = () => {
  const [showNav, setShowNav] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > lastScrollY) {
        setShowNav(false); // scrolling down → hide
      } else {
        setShowNav(false); // scrolling up → show
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Handle WhatsApp message visibility with timeout
  useEffect(() => {
    let timer;
    if (showNav) {
      setShowMessage(true); // show message when nav opens
      timer = setTimeout(() => {
        setShowMessage(false); // hide after 3s
      }, 3000);
    } else {
      setShowMessage(false);
    }

    return () => clearTimeout(timer); // cleanup
  }, [showNav]);

  return (
    <>
      {/* Nav Bar */}
      <AnimatePresence>
        {showNav && (
          <motion.nav
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4 }}
            className="fixed bottom-2 lg:bottom-8 w-full z-[50] flex justify-center"
          >
            <div className="container mx-auto">
              <div className="w-full bg-black/20 h-[96px] backdrop-blur-2xl rounded-full flex max-w-[460px] mx-auto px-5 items-center justify-between text-2xl text-white/50">
                <div
                  onClick={() => setShowNav(false)}
                  className="h-10 w-10 animate-pulse cursor-pointer  text-red-500 absolute top-[-20px] left"
                >
                  <HiChevronDoubleRight />
                </div>

                {navItems.map(({ to, icon, offset = 0 }) => (
                  <Link
                    key={to}
                    to={to}
                    smooth={true}
                    offset={offset}
                    className="hover:bg-purple-600/50 hover:rounded-full flex items-center justify-center w-[60px] h-[60px] cursor-pointer transition-all duration-200"
                  >
                    {icon}
                  </Link>
                ))}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      {!showNav && (
        <motion.button
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 100 }}
          transition={{ duration: 0.3 }}
          onClick={() => setShowNav(true)}
          className="fixed bottom-2 md:bottom-6 h-[100px] right-0 bg-purple-600/50 text-white p-3 rounded-tl-md rounded-bl-md shadow-lg z-[60] flex items-center justify-center"
        >
          <div className="animate-pulse">
            <HiChevronDoubleLeft className="h-5 w-5" />
          </div>
        </motion.button>
      )}

      {/* Lottie Animation + Message */}
      {showNav && (
        <div className="z-30">
          <AnimatePresence>
            {showMessage && (
              <motion.div
                initial={{ x: -200, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -200, opacity: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="fixed bottom-[115px] right-[80px] bg-black/60 text-white px-3 py-1 rounded-md text-sm z-[70]"
              >
                <p>Message me now on WhatsApp! 👉🏽</p>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ x: -90, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            onClick={() =>
              window.open(
                "https://wa.me/2348136593992?text=" +
                  encodeURIComponent(
                    "Hello, I am interested in your services."
                  ),
                "_blank"
              )
            }
            className="fixed bottom-24 right-4 w-16 h-16 z-[70] cursor-pointer hover:scale-110 transition-transform"
          >
            <Lottie animationData={navToggleAnim} loop={true} autoplay={true} />
          </motion.div>
        </div>
      )}
    </>
  );
};

export default Nav;
